import * as React from 'react';
import { useCallback, useRef } from 'react';

/** Material UI Packages. */
import { GridRowModes } from '@mui/x-data-grid-premium';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { deleteRoute, updatePod, updatePol, updateRoute } from 'shared/store/slices/routeSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { deleteSocketData } from 'shared/lib/fetchSocket';

export default function RouteColumnHandlers(props) {
    /** Get props from TransportRoute.js. */
    const { tabValue, rowModesModel, setRowModesModel, apiRef, restoreRouteRef, savePrevRoute } = props;

    /** Load dispatch to update routes. */
    const dispatch = useDispatch();

    /** Route page info. */
    const routeData = useSelector((state) => state.routes);

    /** Handler for converting rowModesModel's mode to edit and setting default focus cell to 'pol_name'. */
    const handleEditClick = (id, api) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
        savePrevRoute(id);
        apiRef.current.setCellFocus(id, 'pol_name');
    };

    /** Handler for converting rowModesModel to view mode. */
    const handleSaveClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    /** Handler for restoring previous data to the route when user clicks the cancel button. */
    const handleCancelClick = (id) => () => {
        // Originally, the default cancel function worked properly when 'ignoreModifications' was set to true.
        // However, becuase customized row edit handlers are now in use, the original approach no longer function as expected.
        // Therefore, we use restoreRoute to save the state before a row update, and this function is triggered when the cacnel button is clicked.
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = routeData.il_route.find((row) => row.id === id);

        if (editedRow.isNew) {
            dispatch(deleteRoute(id));
        } else {
            const prevRoute = restoreRouteRef.current[id];
            if (prevRoute) {
                dispatch(updateRoute({ newRow: prevRoute, updatedRow: prevRoute }));
            } else {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'No previous route found for ID:',
                        severity: 'warning',
                    })
                );
            }
        }
    };

    /** Handler for route removal, remove exist route after window.confirm return true. */
    const handleDeleteClick = useCallback(
        (id) => () => {
            if (window.confirm('Once deleted, this route cannot be recovered. Continue?')) {
                deleteSocketData(dispatch, tabValue, 'row', 'il_route', null, id, () => {
                    dispatch(deleteRoute(id));
                    dispatch(
                        setSnackbar({ open: true, message: 'Successfully deleted the route.', severity: 'success' })
                    );
                });
            }
        },
        [routeData.il_route]
    );

    /** Handler to update location autocomplete component. */
    const handleAutocompleteChange = async (params, newValue) => {
        const { api, id, field } = params;
        // Update the autocomplete within the cell by synchronously updating both the code and country.
        // Since both the local state and gridState must be updated simultaneously, 'updateRows' is used to modify the gridState while 'dispatch' updates the redux state.
        try {
            if (field === 'pol_name') {
                await api.updateRows([
                    { id, pol_code: newValue.code, pol_name: newValue.name, pol_country: newValue.country },
                ]);
                dispatch(updatePol({ id, polValue: newValue }));
            } else if (field === 'pod_name') {
                await api.updateRows([
                    { id, pod_code: newValue.code, pod_name: newValue.name, pod_country: newValue.country },
                ]);
                dispatch(updatePod({ id, podValue: newValue }));
            }
        } catch (error) {
            console.error(error);
        }
    };

    return {
        columnDefFeature: {
            handleSaveClick,
            handleCancelClick,
            handleEditClick,
            handleDeleteClick,
            handleAutocompleteChange,
        },
    };
}
